import React, { useState, useEffect } from "react";
import posthog from "posthog-js";

const InstallButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isIOSNonSafari, setIsIOSNonSafari] = useState(false);
  const [isAndroidNonChrome, setIsAndroidNonChrome] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showAlreadyInstalled, setShowAlreadyInstalled] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isChrome, setIsChrome] = useState(false);

  useEffect(() => {
    // Check if mobile/tablet
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    // Detect iOS
    const iOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    if (iOS) {
      setIsIOS(true);
      const isSafari =
        /Safari/.test(navigator.userAgent) &&
        !/CriOS|FxiOS|EdgiOS|OPiOS/.test(navigator.userAgent);

      if (!isSafari) {
        setIsIOSNonSafari(true);
      }

      // Return early - don't run Android logic on iOS!
      return () => window.removeEventListener("resize", checkMobile);
    }

    // Android - check if Chrome ONLY (not Edge)
    const chromeCheck =
      /Chrome/.test(navigator.userAgent) &&
      /Google Inc/.test(navigator.vendor) &&
      !/Edg/.test(navigator.userAgent);
    setIsChrome(chromeCheck);

    if (!chromeCheck) {
      // Not Chrome on Android (includes Edge, Firefox, etc.)
      setIsAndroidNonChrome(true);
    }

    // Listen for beforeinstallprompt (Chrome only)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const handleInstallClick = async (e) => {
    e.stopPropagation();

    if (isIOSNonSafari) {
      setShowInstructions(!showInstructions);
      return;
    }

    if (isAndroidNonChrome) {
      setShowInstructions(!showInstructions);
      return;
    }

    if (isIOS) {
      // iOS Safari - track when user clicks to see instructions
      posthog.capture("pwa_install_button_clicked", {
        platform: "ios",
        browser: "safari",
      });
      setShowInstructions(!showInstructions);
      return;
    }

    // Chrome on Android
    if (deferredPrompt) {
      // Track install button clicked
      posthog.capture("pwa_install_button_clicked", {
        platform: "android",
        browser: "chrome",
      });

      // We have the prompt available - use it!
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === "accepted") {
          // Track successful install
          posthog.capture("pwa_installed", {
            platform: "android",
            browser: "chrome",
            method: "native_prompt",
          });
          console.log("User accepted install");
        } else {
          // Track install dismissed
          posthog.capture("pwa_install_dismissed", {
            platform: "android",
            browser: "chrome",
          });
        }

        setDeferredPrompt(null);
      } catch (error) {
        console.error("Error showing install prompt:", error);
      }
    } else if (isChrome) {
      // Chrome but no prompt available - likely already installed
      setShowAlreadyInstalled(true);
      setTimeout(() => setShowAlreadyInstalled(false), 3000);
    } else {
      // Fallback for other cases
      setShowInstructions(!showInstructions);
    }
  };

  const handleCopyUrl = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

      // Track URL copied (helps understand if users are following through)
      posthog.capture("pwa_install_url_copied", {
        platform: isIOS ? "ios" : "android",
        browser: isIOS ? "non_safari" : "non_chrome",
      });
    } catch (err) {
      console.error("Failed to copy URL:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = window.location.href;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);

        posthog.capture("pwa_install_url_copied", {
          platform: isIOS ? "ios" : "android",
          browser: isIOS ? "non_safari" : "non_chrome",
        });
      } catch (err) {
        console.error("Fallback copy failed:", err);
      }
      document.body.removeChild(textArea);
    }
  };

  // Close instructions when clicking outside
  useEffect(() => {
    if (!showInstructions) return;
    const handleClickOutside = () => setShowInstructions(false);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showInstructions]);

  // Don't render on desktop
  if (!isMobile) return null;

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={handleInstallClick}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          background: "linear-gradient(90deg, #F97316 0%, #EF4444 100%)",
          color: "#FFFFFF",
          border: "none",
          padding: "16px 24px",
          borderRadius: "16px",
          fontSize: "16px",
          fontWeight: "700",
          cursor: "pointer",
          boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
          transition: "all 150ms ease-in-out",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = "0px 8px 18px rgba(0,0,0,0.25)";
          e.currentTarget.style.background =
            "linear-gradient(90deg, #FB923C 0%, #F97373 100%)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0px 4px 12px rgba(0,0,0,0.2)";
          e.currentTarget.style.background =
            "linear-gradient(90deg, #F97316 0%, #EF4444 100%)";
        }}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = "scale(0.97)";
          e.currentTarget.style.boxShadow = "0px 2px 8px rgba(0,0,0,0.2)";
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = "0px 8px 18px rgba(0,0,0,0.25)";
        }}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M10 2v12m0 0l-4-4m4 4l4-4M3 16h14"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Install App
      </button>

      {/* Already Installed popup */}
      {showAlreadyInstalled && (
        <div
          style={{
            position: "absolute",
            bottom: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#10B981",
            color: "white",
            padding: "12px 16px",
            borderRadius: "12px",
            marginBottom: "12px",
            minWidth: "240px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
            zIndex: 1000,
            textAlign: "center",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2.5"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Already installed!
          </div>

          {/* Tooltip arrow */}
          <div
            style={{
              position: "absolute",
              bottom: "-8px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
              borderTop: "8px solid #10B981",
            }}
          />
        </div>
      )}

      {/* Instructions popup */}
      {showInstructions && (
        <div
          style={{
            position: "absolute",
            bottom: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#1a1a1a",
            color: "white",
            padding: "16px",
            borderRadius: "12px",
            marginBottom: "12px",
            minWidth: "280px",
            maxWidth: "320px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
            zIndex: 1000,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {isIOSNonSafari ? (
            // Non-Safari iOS browser
            <div style={{ fontSize: "14px", lineHeight: "1.6" }}>
              <strong
                style={{
                  display: "block",
                  marginBottom: "12px",
                  fontSize: "15px",
                }}
              >
                Please Switch to Safari
              </strong>
              <p style={{ margin: "0 0 12px 0" }}>
                To install Foodie Call on your iPhone, you need to use Safari.
              </p>

              <button
                onClick={handleCopyUrl}
                style={{
                  width: "100%",
                  padding: "12px",
                  backgroundColor: copied ? "#10B981" : "#0A84FF",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  marginBottom: "12px",
                  transition: "background-color 0.2s ease",
                }}
              >
                {copied ? (
                  <>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                    >
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                    >
                      <rect
                        x="9"
                        y="9"
                        width="13"
                        height="13"
                        rx="2"
                        ry="2"
                      ></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                    Copy URL
                  </>
                )}
              </button>

              <div
                style={{
                  backgroundColor: "rgba(255,255,255,0.1)",
                  padding: "12px",
                  borderRadius: "8px",
                  fontSize: "13px",
                }}
              >
                <strong>Steps:</strong>
                <ol style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                  <li>Tap "Copy URL" above</li>
                  <li>Open Safari</li>
                  <li>Paste the URL and go</li>
                </ol>
              </div>
            </div>
          ) : isAndroidNonChrome ? (
            // Non-Chrome Android browser (Edge, Firefox, Samsung Internet, etc.)
            <div style={{ fontSize: "14px", lineHeight: "1.6" }}>
              <strong
                style={{
                  display: "block",
                  marginBottom: "12px",
                  fontSize: "15px",
                }}
              >
                Please Switch to Chrome
              </strong>
              <p style={{ margin: "0 0 12px 0" }}>
                To install Foodie Call on your Android, you need to use Chrome.
              </p>

              <button
                onClick={handleCopyUrl}
                style={{
                  width: "100%",
                  padding: "12px",
                  backgroundColor: copied ? "#10B981" : "#4285F4",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  marginBottom: "12px",
                  transition: "background-color 0.2s ease",
                }}
              >
                {copied ? (
                  <>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                    >
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                    >
                      <rect
                        x="9"
                        y="9"
                        width="13"
                        height="13"
                        rx="2"
                        ry="2"
                      ></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                    Copy URL
                  </>
                )}
              </button>

              <div
                style={{
                  backgroundColor: "rgba(255,255,255,0.1)",
                  padding: "12px",
                  borderRadius: "8px",
                  fontSize: "13px",
                }}
              >
                <strong>Steps:</strong>
                <ol style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                  <li>Tap "Copy URL" above</li>
                  <li>Open Chrome</li>
                  <li>Paste the URL and go</li>
                </ol>
              </div>
            </div>
          ) : isIOS ? (
            // Safari iOS - CORRECT INSTRUCTIONS
            <div style={{ fontSize: "14px", lineHeight: "1.6" }}>
              <strong
                style={{
                  display: "block",
                  marginBottom: "12px",
                  fontSize: "15px",
                }}
              >
                Install Foodie Call:
              </strong>
              <ol style={{ margin: 0, paddingLeft: "20px" }}>
                <li style={{ marginBottom: "10px" }}>
                  Tap the <strong>menu</strong> button
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      margin: "0 4px",
                      padding: "4px 8px",
                      backgroundColor: "rgba(255,255,255,0.2)",
                      borderRadius: "4px",
                    }}
                  >
                    <svg width="20" height="4" viewBox="0 0 20 4" fill="white">
                      <circle cx="2" cy="2" r="2" />
                      <circle cx="10" cy="2" r="2" />
                      <circle cx="18" cy="2" r="2" />
                    </svg>
                  </span>
                  in the address bar
                </li>
                <li style={{ marginBottom: "10px" }}>
                  Tap <strong>Share</strong>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      margin: "0 4px",
                      padding: "4px 8px",
                      backgroundColor: "rgba(255,255,255,0.2)",
                      borderRadius: "4px",
                    }}
                  >
                    <svg width="16" height="20" viewBox="0 0 16 20" fill="none">
                      <rect
                        x="2"
                        y="8"
                        width="12"
                        height="10"
                        rx="1"
                        stroke="white"
                        strokeWidth="1.5"
                        fill="none"
                      />
                      <path
                        d="M8 12V2M8 2L5 5M8 2L11 5"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  </span>
                </li>
                <li style={{ marginBottom: "10px" }}>
                  Scroll down and tap <strong>"Add to Home Screen"</strong>
                </li>
                <li>
                  Tap <strong>"Add"</strong> to confirm
                </li>
              </ol>
            </div>
          ) : null}

          {/* Tooltip arrow */}
          <div
            style={{
              position: "absolute",
              bottom: "-8px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
              borderTop: "8px solid #1a1a1a",
            }}
          />
        </div>
      )}
    </div>
  );
};

export default InstallButton;
